/**
 * English: Authenticated shell — mobile drawer + desktop hover-hold sidebar (push layout, no overlap).
 * Thai: เปลือกหลัง login — drawer บน mobile, วางเมาส์ค้างที่แถบซ้ายเพื่อขยาย (ดันเนื้อหา ไม่ทับ)
 */
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import Sidebar from './Sidebar';
import Header from './Header';
import { useLayout } from '../../context/LayoutContext';

const SIDEBAR_RAIL_PX = 48; /* w-12 */
const SIDEBAR_EXPANDED_PX = 224; /* w-56 */
const HOVER_ENTER_MS = 50;
const HOVER_LEAVE_MS = 280;

export default function Layout() {
  const { isSidebarOpen, setSidebarOpen, isSidebarPinned, toggleSidebarPinned } = useLayout();

  /** Desktop: expanded while mouse is over the rail (unless pinned) */
  const [sidebarHovered, setSidebarHovered] = useState(false);
  const enterTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const leaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const sidebarExpanded = isSidebarPinned || sidebarHovered;
  const sidebarWidthPx = sidebarExpanded ? SIDEBAR_EXPANDED_PX : SIDEBAR_RAIL_PX;

  const clearTimers = useCallback(() => {
    if (enterTimerRef.current) {
      clearTimeout(enterTimerRef.current);
      enterTimerRef.current = null;
    }
    if (leaveTimerRef.current) {
      clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
  }, []);

  const onSidebarMouseEnter = useCallback(() => {
    if (isSidebarPinned) {
      setSidebarHovered(true);
      return;
    }
    if (leaveTimerRef.current) {
      clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
    if (enterTimerRef.current) return;
    enterTimerRef.current = setTimeout(() => {
      setSidebarHovered(true);
      enterTimerRef.current = null;
    }, HOVER_ENTER_MS);
  }, [isSidebarPinned]);

  const onSidebarMouseLeave = useCallback(() => {
    if (isSidebarPinned) return;
    if (enterTimerRef.current) {
      clearTimeout(enterTimerRef.current);
      enterTimerRef.current = null;
    }
    if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current);
    leaveTimerRef.current = setTimeout(() => {
      setSidebarHovered(false);
      leaveTimerRef.current = null;
    }, HOVER_LEAVE_MS);
  }, [isSidebarPinned]);

  useEffect(() => {
    if (isSidebarPinned) {
      clearTimers();
      setSidebarHovered(true);
    } else {
      setSidebarHovered(false);
    }
  }, [isSidebarPinned, clearTimers]);

  useEffect(() => () => clearTimers(), [clearTimers]);

  return (
    <div className="app-shell-texture flex h-screen overflow-hidden bg-canvas">
      <Transition show={isSidebarOpen} as={Fragment}>
        <Dialog onClose={() => setSidebarOpen(false)} className="relative z-50 md:hidden">
          <TransitionChild
            as={Fragment}
            enter="transition-opacity duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40" />
          </TransitionChild>

          <TransitionChild
            as={Fragment}
            enter="transition-transform duration-200 ease-out"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition-transform duration-200 ease-in"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <DialogPanel className="fixed inset-y-0 left-0 flex w-56">
              <Sidebar forceExpanded onNavigate={() => setSidebarOpen(false)} />
              <button
                type="button"
                onClick={() => setSidebarOpen(false)}
                className="absolute right-[-40px] top-3 rounded-full bg-surface-card/90 p-1.5 text-muted hover:text-body"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </DialogPanel>
          </TransitionChild>
        </Dialog>
      </Transition>

      {/* Desktop: narrow rail — hover to expand; width pushes main content (no overlay) */}
      <div
        className="hidden md:flex shrink-0 h-full overflow-hidden border-r border-app-border/80 bg-app-surface/95 backdrop-blur-xl transition-[width] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] will-change-[width]"
        style={{ width: sidebarWidthPx }}
        onMouseEnter={onSidebarMouseEnter}
        onMouseLeave={onSidebarMouseLeave}
        aria-label="Main navigation"
      >
        <Sidebar
          hoverRail
          expanded={sidebarExpanded}
          onRailToggle={toggleSidebarPinned}
          isPinned={isSidebarPinned}
        />
      </div>

      <div className="flex min-w-0 flex-1 flex-col">
        <Header />
        <main className="relative flex-1 overflow-auto px-4 py-6 md:px-10 md:py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
